// GalaxyScene.tsx — Annotated Edition
// -----------------------------------------------------------------------------
// PURPOSE
// A safe-mode, explorable 3D scene for Pickleball skills visualized as a ring
// of category panels around a rotating core ball. Animated “connections” (plasma
// ribbons) link each panel to the core. “Tip Travellers” are moving label cards
// that follow the connections and show skill tips/drill titles.
//
// WHAT THIS FILE CONTAINS
// - Utilities for tree traversal on your skills JSON
// - A real 3D pickleball core (separate component)
// - Animated Line2 “plasma” connections, with per-vertex gradient colors
// - TipTravellers: moving card labels (Billboard + DreiText + planes)
// - Scene composition (lights, camera, orbit controls, panels, etc.)
// - Config knobs collected in KNOBS (imported), plus scene constants
//
// HOW TO TUNE
// - Appearance of ribbons → see buildPlasmaColors() + widths/opacs in Connections
// - Ribbon motion → see useCurvesShared() + Connections useFrame() inner math
// - Card sizes/wrap → TipTravellers: TARGET_PX, fontSize, maxWidth
// - Overlap logic → TipTravellers: ENABLE_OVERLAP_CULL and screen-cell grid
// - Panel layout → ORBIT_R, ORBIT_Y, PlanetTableOverlay props
// -----------------------------------------------------------------------------

import React, { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
//import { extend } from "@react-three/fiber";

import { Canvas, extend, useFrame } from "@react-three/fiber";
import {
  /* Html, */ Text as DreiText,
  Billboard,
  OrbitControls,
  QuadraticBezierLine,
  Line,
} from "@react-three/drei";
import { useThree } from "@react-three/fiber";

import { PlanetTableOverlay } from "./PlanetTableOverlay"; // Panel cards

// ---- DATA SOURCES (choose one) ------------------------------------------------
// You usually switch between variations of the skills tree. Keep only one import
// active at a time. The idIndex is built from this root.
// import rawTree from "../data/pb-skills-everyone-v8-leaf-minutes.json";
// import rawTree from "../data/pb-skills-everyone-v13-fixed-levels_11-6-954am.json";
//import rawTree from "../data/pickleball_skills_with_60_invisible.json"; //pb-skills-everyone-v13-merged-specialty-shots_11_6_228pm.json";
import rawTree from "../data/pbskills_level60_invisible_descriptive.json"; //pbskills_invis60_updated.json"; //pickleball_skills_with_60_invisible_with_context.json"; //pb-skills-everyone-v13-merged-specialty-shots_11_6_228pm.json";

import { ZProvider } from "./zorder-context"; // z-order context for overlays
import { RealPickleballCore } from "./PickleballCore"; // the 3D core sphere

import type { SkillNode } from "../types";

import { KNOBS } from "./knobs"; // centralized runtime knobs

import LevelTableDialog from "../viz/LevelTableDialog";

// import { Line2, LineGeometry, LineMaterial } from "three-stdlib";

import { Line2, LineMaterial, LineGeometry } from "three-stdlib"; // fat lines


import { Html } from "@react-three/drei";

extend({ Line2, LineGeometry, LineMaterial });

export const skillsRoot = rawTree as unknown as SkillNode;

extend({ LineMaterial, Line2, LineGeometry });

// ---- JSX type extensions for drei's Line2 material ---------------------------
// declare global {
//   namespace JSX {
//     interface IntrinsicElements {
//       lineMaterial: ReactThreeFiber.Object3DNode<
//         LineMaterial,
//         typeof LineMaterial
//       >;
//       line2: ReactThreeFiber.Object3DNode<Line2, typeof Line2>;
//     }
//   }
// }

function HtmlTipCard({
  text,
  alpha = 1,
  widthPx = 360, // target max width in CSS px
  maxLines = 4, // clamp lines
  distanceFactor = 12, // scale vs. camera distance (tweak to taste)
}: {
  text: string;
  alpha?: number;
  widthPx?: number;
  maxLines?: number;
  distanceFactor?: number;
}) {
  return (
    <Html
      transform // <-- world-space (won’t billboard)
      distanceFactor={distanceFactor}
      zIndexRange={[20, 0]} // keep above the scene but under UI
      pointerEvents="none"
      style={{ opacity: alpha, transform: "scale(0.6)" }} // fade as you already compute
    >
      <div
        className="ttCard"
        style={{
          maxWidth: widthPx,
          // fallbacks if you don’t add the CSS block below:
          fontSize: "10px",
          background: "rgba(255,255,255,0.85)",
          border: "1px solid rgba(255,255,255,0.7)",
          borderRadius: 10,
          padding: "8px 10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          color: "#000",
          // multi-line clamp fallback
          display: "-webkit-box",
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          wordBreak: "break-word",
          hyphens: "auto",
          whiteSpace: "pre-wrap",
          backdropFilter: "blur(2px)",
        }}
      >
        {text}
      </div>
    </Html>
  );
}

// ---- UTILS: safe guards for vectors ------------------------------------------
const finite3 = (v: THREE.Vector3) =>
  Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
const clampFinite3 = (v: THREE.Vector3) => {
  if (!Number.isFinite(v.x)) v.x = 0;
  if (!Number.isFinite(v.y)) v.y = 0;
  if (!Number.isFinite(v.z)) v.z = 0;
  return v;
};

// Build a quick id→node index once. Makes lookups O(1) throughout.
const idIndex = buildIdIndex(rawTree as SkillNode);

// -----------------------------------------------------------------------------
// SMALL LINE DEMO (optional): A stand-alone gradient line to verify settings.
// Useful if you suspect resolution/linewidth mismatches.
export function PlasmaLine_NoEllipses() {
  const { size } = useThree();
  const matRef = React.useRef<LineMaterial>(null);

  React.useEffect(() => {
    if (matRef.current) {
      matRef.current.resolution.set(size.width, size.height);
    }
  }, [size.width, size.height]);

  return (
    <Line
      points={[
        [0, 0, 0],
        [1, 1, 0],
        [2, 0.5, 0],
        [3, 1.2, 0],
      ]}
      lineWidth={4}
      color="white"
      dashed={false}
      depthTest={true}
      depthWrite={false}
      transparent={true}
      toneMapped={false}
      frustumCulled={false}
      renderOrder={20}
      onUpdate={(obj: any) => {
        const line2 = obj as unknown as Line2;
        const material = line2.material as LineMaterial | undefined;
        // Keep resolution synced (linewidth in pixels needs this)
        material?.resolution?.set(size.width, size.height);
        if (material) {
          material.transparent = true;
          material.depthTest = true;
          material.depthWrite = false;
          material.toneMapped = false;
          material.blending = THREE.AdditiveBlending;
          (material as any).vertexColors = true; // we’ll provide colors
        }
        (line2 as any).computeLineDistances?.();
      }}
    >
      {/* <lineMaterial
        ref={matRef}
        vertexColors={true}
        transparent={true}
        depthTest={true}
        depthWrite={false}
        toneMapped={false}
        blending={THREE.AdditiveBlending}
      /> */}

      <primitive
        object={new LineMaterial()}
        attach="material"
        ref={matRef as any}
        vertexColors={true}
        transparent={true}
        depthTest={true}
        depthWrite={false}
        toneMapped={false}
        blending={THREE.AdditiveBlending}
      />
    </Line>
  );
}

// -----------------------------------------------------------------------------
// TREE HELPERS: building strings for tip travellers & indices
// -----------------------------------------------------------------------------

type SkillLevel =
  | "3.5"
  | "4.0"
  | "4.5"
  | "5.0"
  | "5.5"
  | "6.0"
  | "6.0 Invisible Skills";
type TipItem = { label: string; level?: SkillLevel; type?: string };

// id → SkillNode index for O(1) lookups
export function buildIdIndex(root: SkillNode): Record<string, SkillNode> {
  const idx: Record<string, SkillNode> = {};
  const walk = (n: SkillNode) => {
    idx[n.id] = n;
    n.children?.forEach(walk);
  };
  walk(root);
  return idx;
}

// Gather all leaves under a node (leaf = no children)
function collectLeaves(node: SkillNode | undefined, out: SkillNode[]) {
  if (!node) return;
  if (!node.children || node.children.length === 0) {
    out.push(node);
    return;
  }
  node.children.forEach((c) => collectLeaves(c, out));
}

// Pull strings (tips + drill titles) for travellers and preserve level & "type"
export function stringsForCategoryWithLevelsAndType(
  catId: string,
  idIndex: Record<string, SkillNode>,
): { label: string; level?: SkillLevel; type?: string }[] {
  const cat = idIndex[catId];
  if (!cat) return [];

  const leaves: SkillNode[] = [];
  collectLeaves(cat, leaves);

  const typeLabel = cat.name; // treat the category's name as a "type" column
  const seen = new Set<string>();
  const items: { label: string; level?: SkillLevel; type?: string }[] = [];

  for (const leaf of leaves) {
    const lvl = leaf.level;

    // tips
    if (leaf.tips?.length) {
      for (const t of leaf.tips) {
        const label = t.trim();
        if (!label || seen.has(label)) continue;
        seen.add(label);
        items.push({ label, level: lvl, type: typeLabel });
      }
    }

    // drill titles
    if (leaf.drills?.length) {
      for (const d of leaf.drills) {
        const title = (d.title ?? "").trim();
        if (!title || seen.has(title)) continue;
        seen.add(title);
        items.push({ label: title, level: lvl, type: typeLabel });
      }
    }
  }
  return items;
}

// A simpler variant (no type)
export function stringsForCategoryWithLevels(
  catId: string,
  idIndex: Record<string, SkillNode>,
): TipItem[] {
  const cat = idIndex[catId];
  if (!cat) return [];

  const leaves: SkillNode[] = [];
  collectLeaves(cat, leaves);

  const seen = new Set<string>();
  const items: TipItem[] = [];

  for (const leaf of leaves) {
    const lvl = leaf.level; // may be undefined on odd data

    // tips → labels
    if (leaf.tips?.length) {
      for (const t of leaf.tips) {
        const label = t.trim();
        if (!label || seen.has(label)) continue;
        seen.add(label);
        items.push({ label, level: lvl });
      }
    }

    // drills → titles
    if (leaf.drills?.length) {
      for (const d of leaf.drills) {
        const title = (d.title ?? "").trim();
        if (!title || seen.has(title)) continue;
        seen.add(title);
        items.push({ label: title, level: lvl });
      }
    }
  }

  return items;
}

// Default top-level categories come from the root's children
const defaultCategories: SkillNode[] = (rawTree as SkillNode).children ?? [];

// Produce compact summaries to pass to PlanetTableOverlay
function summarizeCategories(categories: SkillNode[]) {
  const leavesOf = (n: SkillNode): SkillNode[] =>
    !n.children || n.children.length === 0 ? [n] : n.children.flatMap(leavesOf);

  const minutesForLeaf = (n: SkillNode) =>
    (n.drills ?? []).reduce(
      (s, d: any) => s + (d.minutes ?? d.recommendedMinutes ?? 0),
      0,
    );

  const minutesSum = (nodes: SkillNode[]) =>
    nodes.reduce((s, leaf) => s + minutesForLeaf(leaf), 0);

  return categories.map((cat) => {
    const leaves = leavesOf(cat);
    return {
      id: cat.id,
      title: cat.name,
      leavesCount: leaves.length,
      totalMinutes: minutesSum(leaves),
    };
  });
}

export const isLeaf = (n: SkillNode) => !n.children || n.children.length === 0;
export function flattenLeaves(n: SkillNode): SkillNode[] {
  if (isLeaf(n)) return [n];
  return (n.children ?? []).flatMap(flattenLeaves);
}
export function minutesForLeaf(n: SkillNode): number {
  const drills = n.drills ?? [];
  let total = 0;
  for (const d of drills) {
    const m = (d as any).minutes ?? (d as any).recommendedMinutes ?? 0;
    total += typeof m === "number" ? m : 0;
  }
  return total;
}
export function minutesSum(nodes: SkillNode[]) {
  return nodes.reduce((s, leaf) => s + minutesForLeaf(leaf), 0);
}

// -----------------------------------------------------------------------------
// SCENE CONSTANTS (easy dial points)
// -----------------------------------------------------------------------------
const CORE_RADIUS = 4; // size of the core ball
const CORE_Y = 6; // core height in world units
const ORBIT_R = 18.5; // ring radius for category panels
const ORBIT_Y = -3.8; // ring vertical offset

const CORE_SPIN = 0.5; // spin rate of the core ball
const CURVE_LIFT = KNOBS.curves.lift; // vertical lift for control points
const DASH_FLOW = 0.35; // (legacy dotted-lines param)
const TIP_SPEED = 0.2; // (unused here; travellers use KNOBS)

// Deterministic color based on id (stable hue band)
function colorFromIdHex(id: string) {
  let h = 2166136261 >>> 0; // FNV-ish hash
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const t = (h >>> 0) / 4294967295;
  const hue = (55 + t * 30) / 360; // goldish band
  const c = new THREE.Color();
  c.setHSL(hue, 0.8, 0.6);
  return `#${c.getHexString()}`;
}

// A simple, safe fallback ball (not used if RealPickleballCore is present)
function SafeBall() {
  const g = useRef<THREE.Group>(null!);
  useFrame((_, dt) => {
    if (!g.current) return;
    g.current.rotation.y += CORE_SPIN * dt;
    g.current.rotation.x += CORE_SPIN * 0.36 * dt;
  });
  return (
    <group ref={g} position={[0, 0, 0]}>
      <mesh>
        <sphereGeometry args={[CORE_RADIUS, 64, 64]} />
        <meshStandardMaterial
          color="#D6E24A"
          emissive="#E8F766"
          emissiveIntensity={0.25}
          roughness={0.6}
          metalness={0.0}
          depthWrite={true}
          depthTest={true}
          transparent={false}
        />
      </mesh>
    </group>
  );
}

// -----------------------------------------------------------------------------
// "PLASMA" CONNECTIONS (fat Line2 ribbons with gradients)
// -----------------------------------------------------------------------------

type CurveRef = {
  anchorRef: React.RefObject<THREE.Object3D | null>; // panel anchor (start)
  color: string; // (unused for now; gradient used)
  control: THREE.Vector3; // lifted control point
  end: THREE.Vector3; // core-surface end point
};

// Resolution-independent fat lines want a fixed sample count per curve.
const SAMPLES = 64; // points along each ribbon
const FLOATS = SAMPLES * 3; // xyz per point
const LAYERS = 1; // number of stacked ribbons (core + glows)
const CORE = 0,
  GLOW1 = 1,
  GLOW2 = 2; // names for layers if LAYERS>1

// A gold-ish gradient (so the ribbons read as warm light)
function buildPlasmaColors(n: number) {
  const c0 = new THREE.Color().setHSL(0.11, 0.55, 0.58); // amber
  const c1 = new THREE.Color().setHSL(0.13, 0.4, 0.7); // golden
  const c2 = new THREE.Color().setHSL(0.14, 0.2, 0.88); // warm white

  const out = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const a = c0.clone().lerp(c1, Math.min(1, t * 0.85));
    const b = c1.clone().lerp(c2, Math.max(0, (t - 0.35) / 0.65));
    const col = a.lerp(b, 0.5);
    const j = i * 3;
    out[j + 0] = col.r;
    out[j + 1] = col.g;
    out[j + 2] = col.b;
  }
  return out;
}

// One-time wire-up for a Line2: set positions and per-vertex colors
export function initLine(line: Line2, positions: Float32Array) {
  const geom: any = line.geometry; // LineGeometry
  if (!geom?.setPositions) return;

  if (positions.length !== FLOATS) {
    throw new Error(
      `initLine: positions length=${positions.length}, expected=${FLOATS}`,
    );
  }
  geom.setPositions(positions);

  const colors = buildPlasmaColors(SAMPLES);
  const hasSetColors = typeof (geom as any).setColors === "function";
  if (hasSetColors) {
    (geom as any).setColors(colors);
  } else {
    // Fallback: add a BufferAttribute called 'color'
    if (!geom.getAttribute("color")) {
      geom.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    } else {
      const attr = geom.getAttribute("color") as THREE.BufferAttribute;
      attr.copyArray(colors);
      attr.needsUpdate = true;
    }
  }

  // Configure material lazily (it might not exist on first tick)
  const applyMat = () => {
    const mat = line.material as LineMaterial | undefined;
    if (!mat) return false;
    mat.transparent = true;
    mat.depthTest = true;
    mat.depthWrite = false;
    mat.toneMapped = false;
    mat.blending = THREE.AdditiveBlending;
    (mat as any).vertexColors = true;
    return true;
  };
  if (!applyMat()) requestAnimationFrame(() => applyMat());

  line.computeLineDistances?.();
}

// Tiny fast noise for jitter
function pseudoNoise1D(t: number, seed: number) {
  const x = Math.sin((t + seed) * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

// Type guard for drei's Line2
function isLine2(o: unknown): o is Line2 {
  return !!o && typeof o === "object" && (o as any).isLine2 === true;
}

// The component that draws/animates all ribbons each frame
export function Connections({
  curves,
}: {
  curves: React.MutableRefObject<CurveRef[]>;
}) {
  const { size } = useThree();

  // per-curve references to three Line2 objects (one per layer) and buffers
  const lineRefs = useRef<(Line2 | any)[][]>([]);
  const buffers = useRef<Float32Array[][]>([]); // [curve][layer] → positions
  const starts = useRef<THREE.Vector3[]>([]);
  const colors = useRef<Float32Array[]>([]); // per-vertex colors

  // Ensure arrays have desired length without reassigning the refs
  const ensureLen = <T,>(arr: T[], n: number, make: () => T) => {
    while (arr.length < n) arr.push(make());
    if (arr.length > n) arr.length = n;
  };
  const ensureCurve = (i: number) => {
    if (!lineRefs.current[i])
      lineRefs.current[i] = Array.from({ length: LAYERS }, () => null as any);
    if (!buffers.current[i])
      buffers.current[i] = Array.from(
        { length: LAYERS },
        () => new Float32Array(FLOATS),
      );
    if (!starts.current[i]) starts.current[i] = new THREE.Vector3();
    if (!colors.current[i]) colors.current[i] = new Float32Array(SAMPLES * 3);
  };

  // Keep ref arrays sized when number of curves changes
  useEffect(() => {
    const n = curves.current.length;
    ensureLen(lineRefs.current as any, n, () =>
      Array.from({ length: LAYERS }, () => null as any),
    );
    ensureLen(starts.current, n, () => new THREE.Vector3());
    ensureLen(colors.current, n, () => new Float32Array(SAMPLES * 3));
  }, [curves]);

  // Scratch vectors reused every frame (avoid GC churn)
  const a = useRef(new THREE.Vector3()).current; // start
  const b = useRef(new THREE.Vector3()).current; // control
  const e = useRef(new THREE.Vector3()).current; // end
  const p = useRef(new THREE.Vector3()).current; // working point
  const prev = useRef(new THREE.Vector3()).current; // previous sample
  const tangent = useRef(new THREE.Vector3()).current;
  const side = useRef(new THREE.Vector3()).current;
  const up = new THREE.Vector3(0, 1, 0);

  // Visual parameters per layer (thin core, thicker glow levels)
  const widths = [2.6, 5.2, 8.6];
  const opacs = [0.95, 0.36, 0.18];

  // Main animation loop: recompute sampled ribbons each frame
  useFrame(() => {
    const now = performance.now() * 0.001;
    const n = curves.current.length;

    for (let i = 0; i < n; i++) {
      const c = curves.current[i];
      if (!c?.anchorRef?.current) continue;
      ensureCurve(i);

      // Sample the current world positions for this curve
      c.anchorRef.current.getWorldPosition(a); // start
      b.copy(c.control); // control
      e.copy(c.end); // end on core

      // If bad numbers sneak in, clamp to zeros so line doesn't explode
      if (!finite3(a)) a.set(0, 0, 0);
      if (!finite3(b)) b.set(0, 0, 0);
      if (!finite3(e)) e.set(0, 0, 0);

      for (let L = 0; L < LAYERS; L++) {
        const line: any = lineRefs.current[i][L];
        if (!line) continue;
        prev.set(0, 0, 0);

        const arr = buffers.current[i][L];
        let ptr = 0;

        // Layer-specific wobble amplitude (outer glows wiggle more)
        let wobbleAmp = 0.5;
        if (L === 1) wobbleAmp *= 1.35;
        if (L === 2) wobbleAmp *= 1.75;

        const seed = i + L * 17.3; // cheap distinct seed per layer
        let col = new THREE.Color();

        for (let k = 0; k < SAMPLES; k++) {
          const t = k / (SAMPLES - 1);
          const it = 1 - t;

          // Quadratic Bezier point at t (a→b→e)
          p.set(
            it * it * a.x + 2 * it * t * b.x + t * t * e.x,
            it * it * a.y + 2 * it * t * b.y + t * t * e.y,
            it * it * a.z + 2 * it * t * b.z + t * t * e.z,
          );

          // Tangent and stable perpendicular basis (side/binormal)
          if (k === 0) prev.copy(p);
          tangent.copy(p).sub(prev);
          const len = tangent.length();
          if (len > 1e-9) tangent.multiplyScalar(1 / len);
          else tangent.set(0, 0, 0);
          prev.copy(p);

          side.copy(tangent).cross(up);
          if (
            !Number.isFinite(side.x) ||
            !Number.isFinite(side.y) ||
            !Number.isFinite(side.z) ||
            side.lengthSq() < 1e-12
          ) {
            side.set(1, 0, 0);
          } else {
            side.multiplyScalar(1 / Math.sqrt(side.lengthSq()));
          }
          const binormal = new THREE.Vector3().copy(side).cross(tangent);
          if (binormal.lengthSq() > 1e-10) binormal.normalize();
          else binormal.set(0, 1, 0);

          // Bell-shaped amplitude (0 at ends, 1 mid), with edge lift
          const bell = Math.sin(Math.PI * t);
          const edgeBoost = 0.25 + 0.35 * (1 - Math.abs(0.5 - t) * 2);
          const falloff = THREE.MathUtils.clamp(
            0.15 + 1.1 * bell + 0.35 * edgeBoost,
            0,
            1.7,
          );

          // Layered motion terms — looks alive without heavy noise
          const waveK = 8.0; // spatial freq along the strand
          const axial = Math.sin(now * 1.4 + seed * 0.3 + t * waveK);
          const curl = Math.cos(now * 0.9 + seed * 0.7 + t * (waveK * 0.7));

          const n1 = pseudoNoise1D(now * 0.9 + t * 6.0, seed);
          const n2 = pseudoNoise1D(now * 1.7 + t * 13.0, seed + 99.0);
          const n3 = Math.sin((now * 1.2 + t * 9.0 + seed) * 2.0) * 0.5 + 0.5;
          const jitter = (n1 - 0.5) * 0.8 + (n2 - 0.5) * 0.5 + (n3 - 0.5) * 0.3;

          // Sideways wiggle + out-of-plane curl
          const offsetSide = wobbleAmp * falloff * (0.7 * jitter + 0.3 * axial);
          const offsetBin = wobbleAmp * falloff * 0.55 * curl;
          p.addScaledVector(side, offsetSide);
          p.addScaledVector(binormal, offsetBin);

          // Extra kick near the panel end to feel “launched”
          if (t > 0.0 && t < 0.2) {
            const kEdge = (0.2 - t) / 0.2; // 1→0 over first 20%
            const flick = 0.45 * kEdge * Math.sin(now * (2.1 + 0.3 * i) + seed);
            p.addScaledVector(side, flick);
          }

          // Write position, guarding against NaNs/Infs
          arr[ptr++] = Number.isFinite(p.x) ? p.x : 0;
          arr[ptr++] = Number.isFinite(p.y) ? p.y : 0;
          arr[ptr++] = Number.isFinite(p.z) ? p.z : 0;

          // Per-vertex gradient color
          gradAt(t, col);
          const cbuf = colors.current[i];
          cbuf[k * 3 + 0] = col.r;
          cbuf[k * 3 + 1] = col.g;
          cbuf[k * 3 + 2] = col.b;
        }

        // Push positions + colors to GPU
        const g: any = line.geometry;
        if (g?.setPositions) {
          g.setPositions(arr);
          if (typeof g.setColors === "function") {
            g.setColors(colors.current[i]);
          } else {
            const attr = g.getAttribute("color") as
              | THREE.BufferAttribute
              | undefined;
            if (!attr) {
              g.setAttribute(
                "color",
                new THREE.Float32BufferAttribute(colors.current[i], 3),
              );
            } else {
              attr.copyArray(colors.current[i]);
              attr.needsUpdate = true;
            }
          }
          line.computeLineDistances?.();
        }

        // Tweak material every frame for flicker/opacity and DPR changes
        const m: any = line.material;
        if (m) {
          m.transparent = true;
          m.depthTest = true;
          m.depthWrite = false;
          m.toneMapped = false;
          m.blending = THREE.AdditiveBlending;
          (m as any).linewidth = widths[L];
          (m as any).opacity =
            opacs[L] * (0.85 + 0.15 * Math.sin(now * 1.1 + i + L));
          m.resolution?.set(size.width, size.height);
        }
      }
    }
  });

  // Smooth gold ramp sampled via t
  const C0 = new THREE.Color().setHSL(0.11, 0.55, 0.58);
  const C1 = new THREE.Color().setHSL(0.12, 0.45, 0.66);
  const C2 = new THREE.Color().setHSL(0.13, 0.35, 0.78);
  const C3 = new THREE.Color().setHSL(0.14, 0.25, 0.88);
  const gradAt = (t: number, out = new THREE.Color()) => {
    if (t < 0.33) return out.copy(C0).lerp(C1, t / 0.33);
    if (t < 0.66) return out.copy(C1).lerp(C2, (t - 0.33) / 0.33);
    return out.copy(C2).lerp(C3, (t - 0.66) / 0.34);
  };

  // Instantiate empty Line components, then patch their geometry/material in onUpdate
  return (
    <>
      {curves.current.map((_, i) => (
        <group key={`plasma-${i}`}>
          {[CORE].map((L, ll) => (
            <Line
              key={"glow-" + ll + "-" + i}
              points={Array.from(
                { length: SAMPLES },
                () => [0, 0, 0] as [number, number, number],
              )}
              lineWidth={L === CORE ? 2.6 : L === GLOW1 ? 5.2 : 8.6}
              dashed={false}
              depthTest={true}
              depthWrite={false}
              transparent={true}
              toneMapped={false}
              frustumCulled={false}
              renderOrder={20}
              onUpdate={(obj: any) => {
                const line = obj as unknown as Line2;
                ensureCurve(i);
                lineRefs.current[i][L] = line; // store the instance

                if (!buffers.current[i][L])
                  buffers.current[i][L] = new Float32Array(FLOATS);
                initLine(line, buffers.current[i][L]); // one-time geometry+colors

                const mat = line.material as LineMaterial | undefined;
                mat?.resolution?.set(size.width, size.height);
                if (mat) {
                  (mat as any).vertexColors = true;
                  mat.transparent = true;
                  mat.depthTest = true;
                  mat.depthWrite = false;
                  mat.toneMapped = false;
                  mat.blending = THREE.AdditiveBlending;
                  (mat as any).linewidth = L === 0 ? 2.6 : L === 1 ? 5.2 : 8.6;
                  (mat as any).opacity = 1.0;
                  mat.color?.set?.(0xffffff); // white multiply
                  mat.needsUpdate = true;
                }
                (line as any).computeLineDistances?.();
              }}
            >
              {/* <lineMaterial
                vertexColors={true}
                transparent={true}
                depthTest={true}
                depthWrite={false}
                toneMapped={false}
                blending={THREE.AdditiveBlending}
              /> */}

              <primitive
                object={new LineMaterial()}
                attach="material"
                //ref={matRef as any}
                vertexColors={true}
                transparent={true}
                depthTest={true}
                depthWrite={false}
                toneMapped={false}
                blending={THREE.AdditiveBlending}
                // vertexColors={true}
                // transparent={true}
                // depthTest={true}
                // depthWrite={false}
                // toneMapped={false}
                // blending={THREE.AdditiveBlending}
              />
            </Line>
          ))}
        </group>
      ))}
    </>
  );
}

// -----------------------------------------------------------------------------
// TIP TRAVELLERS — moving label cards that ride the curves
// -----------------------------------------------------------------------------

type TT_Emitter = { color: string; texts: TipItem[] };

type Rider = {
  eIdx: number; // which emitter/curve am I riding
  phase: number; // initial offset around the curve [0..1)
  t: number; // current progress [0..1)
  prevT: number; // previous t (to detect wrap)
  textIdx: number; // index into emitters[eIdx].texts
  pos: THREE.Vector3; // current world position (cache)
};

type TipTravellersProps = {
  curves: React.MutableRefObject<CurveRef[]>;
  emitters: TT_Emitter[]; // parallel to curves
  speed?: number; // 0.8 = brisk
  ridersPerEmitter?: number; // how many cards per curve
  camera: THREE.PerspectiveCamera; // for sizing + backface detection
  viewportHeight: number; // CSS px → world-units conversion
  dpr?: number; // device pixel ratio
};

// Put these where softenForWrap/hardWrapLongTokens were

// const ZWSP = "\u200b"; // zero-width space
// const SOFT_HYPH = "\u00AD"; // soft hyphen
// const NBSP = "\u00A0";

// // Normalize a few separators Troika doesn't naturally break on
// const EN_DASH = "\u2013";
// const EM_DASH = "\u2014";
// export function normalizeSeparators(s: string) {
//   return s
//     .replace(/\u00A0/g, " ") // NBSP → space
//     .replace(new RegExp(EN_DASH, "g"), "-")
//     .replace(new RegExp(EM_DASH, "g"), "-");
// }

// // Lightweight hyphenation: try Intl.Segmenter, fall back to CV pattern
// export const simpleHyphenation: any = (word: string) => {
//   // Already has break ops?
//   if (word.length <= 14 || /[\u200B\u00AD]/.test(word)) return null;

//   try {
//     // Break at grapheme boundaries, then recombine into ~10–12 chunks
//     const seg = (Intl as any).Segmenter
//       ? new (Intl as any).Segmenter("en", { granularity: "grapheme" })
//       : null;
//     const parts = seg
//       ? Array.from(seg.segment(word), (x: any) => x.segment)
//       : word.split("");

//     const chunk = 10;
//     const hyphens: number[] = [];
//     for (let i = chunk; i < parts.length - 4; i += chunk) {
//       hyphens.push(i);
//     }
//     // Return array of indices where Troika may hyphenate
//     // (counted in codepoints, not JS .length, but good enough here)
//     return hyphens.length ? hyphens : null;
//   } catch {
//     // Consonant-vowel fallback splits (very lax)
//     const hits: number[] = [];
//     const m = /[aeiouy]{1,2}/gi;
//     let match: RegExpExecArray | null;
//     while ((match = m.exec(word))) {
//       const cut = match.index + match[0].length;
//       if (
//         cut > 6 &&
//         cut < word.length - 4 &&
//         (hits.length === 0 || cut - hits[hits.length - 1] > 6)
//       ) {
//         hits.push(cut);
//       }
//     }
//     return hits.length ? hits : null;
//   }
// };
// // Adds soft break opportunities at many sensible boundaries
// export function softenForWrap(s: string): string {
//   if (!s) return s;

//   // Normalize whitespace (troika respects NBSP; convert to regular space)
//   s = s
//     .replace(/\r\n/g, "\n")
//     .replace(/\t/g, " ")
//     .replace(new RegExp(NBSP, "g"), " ");
//   // collapse inner runs but keep explicit newlines
//   s = s
//     .split("\n")
//     .map((line) => line.replace(/ {2,}/g, " ").trim())
//     .join("\n");

//   // camelCase aB → a​B
//   s = s.replace(/([a-z])([A-Z])/g, `$1${ZWSP}$2`);
//   // 3rdShot / Q3Results → break between digits and letters
//   s = s
//     .replace(/([0-9])([A-Za-z])/g, `$1${ZWSP}$2`)
//     .replace(/([A-Za-z])([0-9])/g, `$1${ZWSP}$2`);

//   // Break *after* common separators (don’t remove the separator)
//   s = s.replace(/([:/._\-+|@#&=])/g, `$1${ZWSP}`);

//   // Also allow breaks after ) ] } and ,
//   s = s.replace(/([)\]},])/g, `$1${ZWSP}`);

//   return s;
// }

// // ---- Troika preprocessor: normalize + add soft-breaks (runs before layout)
// export function wrapPreprocessor(raw: string) {
//   // split middots to separate lines first (helps early wrap)
//   let s = raw.replace(/\s*·\s*/g, " ·\n");
//   s = normalizeSeparators(s).replace(/\r\n/g, "\n");
//   s = softenForWrap(s); // add ZWSPs at sensible spots
//   s = hardWrapLongTokensPreservingBreaks(s, 14); // force-break monster tokens
//   return s;
// }

// // Forces breaks inside truly long tokens while keeping existing \n
// export function hardWrapLongTokensPreservingBreaks(
//   s: string,
//   maxChunk = 14
// ): string {
//   return s
//     .split("\n")
//     .map((line) =>
//       line
//         .split(/\s+/)
//         .map((tok) => {
//           // already has opportunities?
//           if (
//             tok.length <= maxChunk ||
//             tok.includes(ZWSP) ||
//             tok.includes(SOFT_HYPH)
//           )
//             return tok;
//           // inject soft hyphens + ZWSPs
//           let out = "";
//           for (let i = 0; i < tok.length; i += maxChunk) {
//             const slice = tok.slice(i, i + maxChunk);
//             out += (out ? SOFT_HYPH + ZWSP : "") + slice;
//           }
//           return out;
//         })
//         .join(" ")
//     )
//     .join("\n");
// }

// /** Break every line into chunks of at most N words. */
// function splitWordsByCount(s: string, maxWords = 4): string {
//   return s
//     .split("\n")
//     .map((line) => {
//       const words = line.trim().split(/\s+/).filter(Boolean);
//       if (words.length <= maxWords) return line.trim();
//       const out: string[] = [];
//       for (let i = 0; i < words.length; i += maxWords) {
//         out.push(words.slice(i, i + maxWords).join(" "));
//       }
//       return out.join("\n");
//     })
//     .join("\n");
// }
// /** Split each line into chunks of ≤ maxWords, but avoid splitting "hard" tokens. */
// function splitWordsByCountSmart(s: string, maxWords = 4): string {
//   const HARD = /[:/@._#?=&\\]/; // tokens containing these won't be split
//   const SEP = /^\u00B7$|^·$/; // middot as its own token

//   return s
//     .split("\n")
//     .map((line) => {
//       const tokens = line.trim().split(/\s+/).filter(Boolean);
//       if (!tokens.length) return "";

//       const chunks: string[] = [];
//       let buf: string[] = [];

//       const flush = () => {
//         if (!buf.length) return;
//         // widow fix: if last chunk would be a single word, pull one back
//         if (buf.length === 1 && chunks.length) {
//           const prev = chunks.pop()!;
//           const prevWords = prev.split(/\s+/);
//           const moved = prevWords.pop()!;
//           chunks.push(prevWords.join(" "));
//           buf.unshift(moved);
//         }
//         chunks.push(buf.join(" "));
//         buf = [];
//       };

//       for (const tok of tokens) {
//         // keep separators as line boundaries
//         if (SEP.test(tok)) {
//           flush();
//           chunks.push("·");
//           continue;
//         }

//         // if token is “hard”, force a new line if it would overflow
//         if (HARD.test(tok)) {
//           if (buf.length) flush();
//           chunks.push(tok);
//           continue;
//         }

//         buf.push(tok);
//         if (buf.length >= maxWords) flush();
//       }
//       flush();
//       return chunks.join("\n");
//     })
//     .join("\n");
// }

// Quadratic Bezier derivative (tangent): B'(t) = 2(1-t)(b-a) + 2t(e-b)
function bezierTangent(
  a: THREE.Vector3,
  b: THREE.Vector3,
  e: THREE.Vector3,
  t: number,
  out = new THREE.Vector3(),
) {
  const it = 1 - t;
  out
    .copy(b)
    .sub(a)
    .multiplyScalar(2 * it)
    .add(
      new THREE.Vector3()
        .copy(e)
        .sub(b)
        .multiplyScalar(2 * t),
    );
  const len = out.length();
  return len > 1e-9 ? out.multiplyScalar(1 / len) : out.set(1, 0, 0); // safe fallback
}

// Helper to build wrapped text (reuse your existing utilities)
function buildWrappedLabel(item?: TipItem & { type?: string }) {
  if (!item) return "";
  let raw = `${item.level ? `${item.level} · ` : ""}${item.label}${
    item.type ? ` · ${item.type}` : ""
  }`;
  return raw;
  //   raw = raw.replace(/\s*·\s*/g, " ·\n");
  //   raw = normalizeSeparators(raw).replace(/\r\n/g, "\n");
  //   return splitWordsByCountSmart(
  //     hardWrapLongTokensPreservingBreaks(softenForWrap(raw), 14),
  //     3
  //   );
}

/** Force cards to be parallel to the category panel they come from.
 *  - Z points radially OUT from the scene center (same as panel face)
 *  - Y is world-up (flat/horizontal)
 *  - X completes the right-handed basis
 */
function orientCardGroup(
  g: THREE.Group,
  start: THREE.Vector3,
  control: THREE.Vector3,
  end: THREE.Vector3,
  t: number,
  _coreY = CORE_Y,
) {
  // point on curve at t (same as before)
  const it = 1 - t;
  const P = new THREE.Vector3(
    it * it * start.x + 2 * it * t * control.x + t * t * end.x,
    it * it * start.y + 2 * it * t * control.y + t * t * end.y,
    it * it * start.z + 2 * it * t * control.z + t * t * end.z,
  );

  // Z = “panel face” direction = radial-out on the XZ plane
  const Z = new THREE.Vector3(P.x, 0, P.z);
  if (Z.lengthSq() < 1e-10) Z.set(1, 0, 0);
  Z.normalize();

  // Y = world up, keep cards perfectly horizontal
  const Y = new THREE.Vector3(0, 1, 0);

  // X completes orthonormal basis (right-handed)
  const X = new THREE.Vector3().copy(Y).cross(Z);
  if (X.lengthSq() < 1e-10) X.set(1, 0, 0);
  X.normalize();

  // build basis and apply
  const basis = new THREE.Matrix4().makeBasis(X, Y, Z);
  g.position.copy(P);
  g.quaternion.setFromRotationMatrix(basis);
}

export function TipTravellers({
  curves,
  emitters,
  speed = 0.8,
  ridersPerEmitter = 2,
  camera,
  viewportHeight,
  dpr = 1,
}: TipTravellersProps) {
  const ENABLE_OVERLAP_CULL = false;
  const MAX_VISIBLE = 18;
  const CELL_PX = 220;

  const ALPHA_BASE = 0.65;

  // Keep travellers away from the center to avoid the "card volcano"
  const T_MIN = 0.06; // don't start exactly at the panel face
  const T_MAX = 1.2; // never enter the inner zone near the core
  const WRAP_ADVANCE = 0.18; // shift a bit on wrap to avoid overlap

  // desired CSS px height of a card at the start vs near the core
  const START_CARD_PX = 52; // smaller than before at the panel (used to feel ~240–360)
  const END_CARD_PX = 52; // even smaller as it approaches the core

  // clamp world scale (keeps cards from exploding on close/far distances)
  const MIN_S = 0.32;
  const MAX_S = 0.78;

  const SHRINK_START = 0.12;
  const SHRINK_END = 0.62;
  const CARD_SCALE = 0.85;
  const TARGET_PX = 240;

  // ----- refs/state wiring ----------------------------------------------------
  const COUNT = Math.max(1, emitters.length * ridersPerEmitter);

  //const groupRefs = useRef<React.RefObject<THREE.Group>[]>([]);
  const groupRefs = useRef<React.RefObject<THREE.Group | null>[]>([]);

  const fadeRef = useRef<number[]>(Array(COUNT).fill(1));

  // prepared strings and last-applied alpha (internal caches to diff against)
  const preparedRef = useRef<string[]>(Array(COUNT).fill(""));
  const lastAlphaRef = useRef<number[]>(Array(COUNT).fill(1));

  // ✅ WHAT THE JSX READS:
  const [uiTexts, setUiTexts] = useState<string[]>(Array(COUNT).fill(""));
  const [uiAlpha, setUiAlpha] = useState<number[]>(Array(COUNT).fill(1));

  // keep arrays sized when COUNT changes
  useEffect(() => {
    while (groupRefs.current.length < COUNT)
      groupRefs.current.push(React.createRef());
    groupRefs.current.length = COUNT;

    preparedRef.current.length = COUNT;
    lastAlphaRef.current.length = COUNT;
    fadeRef.current.length = COUNT;

    setUiTexts((prev) => {
      const next = prev.slice(0, COUNT);
      while (next.length < COUNT) next.push("");
      return next;
    });
    setUiAlpha((prev) => {
      const next = prev.slice(0, COUNT);
      while (next.length < COUNT) next.push(1);
      return next;
    });
  }, [COUNT]);

  // ----- riders init ----------------------------------------------------------
  type Rider = {
    eIdx: number;
    phase: number;
    t: number;
    prevT: number;
    textIdx: number;
    pos: THREE.Vector3;
  };
  const riders = useRef<Rider[]>([]);
  useEffect(() => {
    const seeded: Rider[] = [];
    emitters.forEach((_, eIdx) => {
      for (let k = 0; k < ridersPerEmitter; k++) {
        const phase = (k / ridersPerEmitter + eIdx * 0.173) % 1;
        if (k > 0 && ridersPerEmitter > 1 && (phase < 0.18 || phase > 0.82))
          continue; // avoid starts too near ends

        seeded.push({
          eIdx,
          phase: phase,
          t: 0,
          prevT: 0,
          textIdx: 0,
          pos: new THREE.Vector3(),
        });
      }
    });
    riders.current = seeded.slice(0, COUNT);
    riders.current.forEach((r) => {
      r.t = r.phase;
      r.prevT = r.t;
      const len = emitters[r.eIdx]?.texts.length ?? 0;
      if (len > 0) r.textIdx = Math.floor(r.phase * len) % len;
    });
  }, [emitters, ridersPerEmitter, COUNT]);

  // ----- animation loop -------------------------------------------------------

  const center = new THREE.Vector3(0, CORE_Y, 0);
  const toScreen = (
    p: THREE.Vector3,
    cam: THREE.PerspectiveCamera,
    vw: number,
    vh: number,
  ) => {
    const v = p.clone().project(cam);
    return { x: (v.x * 0.5 + 0.5) * vw, y: (-v.y * 0.5 + 0.5) * vh };
  };
  const cellKey = (sx: number, sy: number) =>
    `${Math.floor(sx / CELL_PX)}:${Math.floor(sy / CELL_PX)}`;

  const levelScore = (lvl?: SkillLevel) =>
    lvl === "6.0"
      ? 5
      : lvl === "5.5"
        ? 4
        : lvl === "5.0"
          ? 3
          : lvl === "4.5"
            ? 2
            : lvl === "4.0"
              ? 1
              : 0;

  const cam = camera as THREE.PerspectiveCamera;
  const scaleForPixelsAtDist = (dist: number, px: number) => {
    const vFOV = (cam.fov * Math.PI) / 180;
    const cssHeight = viewportHeight / Math.max(1, dpr);
    const worldPerCssPx = (2 * Math.tan(vFOV / 2) * dist) / cssHeight;
    return px * worldPerCssPx;
  };

  useFrame((_, dt) => {
    const adv = Math.min(0.05, Math.max(0.0005, dt)) * speed;

    let dirtyText = false;
    let dirtyAlpha = false;
    const nextTexts = uiTexts.slice();
    const nextAlpha = uiAlpha.slice();

    const vh = viewportHeight;
    const vw = (camera as any).aspect * vh;
    const grid = new Map<string, number>();
    const prio = new Map<number, number>();
    const show = new Array(riders.current.length).fill(true);
    let shownCount = 0;

    const camW = new THREE.Vector3();
    cam.getWorldPosition(camW);
    const riderW = new THREE.Vector3();
    const toCam = new THREE.Vector3();
    const normal = new THREE.Vector3();

    for (let i = 0; i < riders.current.length; i++) {
      const r = riders.current[i];
      const curve = curves.current[r.eIdx];
      const em = emitters[r.eIdx];
      if (!curve || !em) continue;

      // advance / wrap
      let tNext = r.t + adv;
      let wrapped = false;

      // ping-pong forward-only across [T_MIN .. T_MAX]; when we hit T_MAX, "wrap" back to T_MIN
      if (tNext > T_MAX) {
        tNext = T_MIN + (tNext - T_MAX) + WRAP_ADVANCE; // leap forward a hair so we don't re-use same spot
        if (tNext > T_MAX) tNext = T_MIN + 0.01;
        wrapped = true;
      }
      if (tNext < T_MIN) tNext = T_MIN;

      r.prevT = r.t;
      r.t = tNext;

      if (wrapped && em.texts.length) {
        r.textIdx = (r.textIdx + 1) % em.texts.length;
      }
      // Bezier point
      const start = new THREE.Vector3();
      curve.anchorRef.current?.getWorldPosition(start);
      const control = curve.control,
        end = curve.end;
      const it = 1 - r.t;
      r.pos.set(
        it * it * start.x + 2 * it * r.t * control.x + r.t * r.t * end.x,
        it * it * start.y + 2 * it * r.t * control.y + r.t * r.t * end.y,
        it * it * start.z + 2 * it * r.t * control.z + r.t * r.t * end.z,
      );

      // place/orient/scale card group
      const g = groupRefs.current[i].current;
      if (g) {
        g.position.copy(r.pos);

        // A — ORIENTATION BLOCK (call site)
        orientCardGroup(g, start, control, end, r.t, CORE_Y);

        g.getWorldPosition(riderW);
        const dist = camW.distanceTo(riderW);
        // Normalize r.t into [0..1] only across the travel band we use
        const u = THREE.MathUtils.clamp(
          (r.t - T_MIN) / Math.max(1e-6, T_MAX - T_MIN),
          0,
          1,
        );

        // As cards move from panel → core, reduce their target CSS pixels
        const targetPx = THREE.MathUtils.lerp(
          START_CARD_PX,
          END_CARD_PX,
          THREE.MathUtils.smoothstep(0, 1, u),
        );

        // Convert desired CSS pixels to world units at the rider’s current distance
        let s = scaleForPixelsAtDist(dist, targetPx);

        // Keep within sensible bounds and apply your global multiplier
        s = THREE.MathUtils.clamp(s, MIN_S, MAX_S);
        g.scale.setScalar(s * CARD_SCALE);

        // backface fade
        normal.copy(riderW).sub(center).normalize();
        toCam.copy(camW).sub(center).normalize();
        const dot = normal.dot(toCam);
        const raw = THREE.MathUtils.clamp((dot + 0.45) / (0.1 + 0.45), 0, 1);
        const GHOST = 0.62;
        fadeRef.current[i] = GHOST + (1 - GHOST) * raw;

        // culling grid (optional)
        const { x: sx, y: sy } = toScreen(riderW, cam, vw, vh);
        const key = cellKey(sx, sy);
        const item = em.texts[r.textIdx];
        const priority =
          (fadeRef.current[i] ?? 1) * 100 +
          levelScore(item?.level) * 10 -
          dist * 0.01;
        prio.set(i, priority);
        if (ENABLE_OVERLAP_CULL) {
          const prev = grid.get(key);
          if (prev === undefined) {
            if (shownCount < MAX_VISIBLE) {
              grid.set(key, i);
              show[i] = true;
              shownCount++;
            }
          } else if ((prio.get(prev) ?? -Infinity) < priority) {
            show[prev] = false;
            grid.set(key, i);
            show[i] = true;
          }
        } else {
          show[i] = true;
        }
      }

      // prepare wrapped label string
      const item = em.texts[r.textIdx];
      const prepared = buildWrappedLabel(item);
      if (prepared !== preparedRef.current[i]) {
        preparedRef.current[i] = prepared;
        nextTexts[i] = prepared;
        dirtyText = true;
      }

      // final alpha (what HtmlTipCard will read)
      const hemi = fadeRef.current[i] ?? 1;
      let alpha = (show[i] ? 1.0 : 0.0) * Math.max(0.15, hemi);

      const gp = new THREE.Vector3();
      groupRefs.current[i].current?.getWorldPosition(gp);
      const dist = camW.distanceTo(gp);
      const tierCore = 1.0 - THREE.MathUtils.smoothstep(SHRINK_START, 1.0, r.t);
      const distFade = THREE.MathUtils.clamp(
        1.0 - THREE.MathUtils.smoothstep(28, 50, dist),
        0.25,
        1.0,
      );

      alpha *= ALPHA_BASE * 0.9 * (0.6 + 0.4 * tierCore) * distFade;
      alpha = THREE.MathUtils.clamp(alpha, 0, 1);

      if (alpha !== lastAlphaRef.current[i]) {
        lastAlphaRef.current[i] = alpha;
        nextAlpha[i] = alpha;
        dirtyAlpha = true;
      }
    }

    // batch commit state once per frame if anything changed
    if (dirtyText) setUiTexts(nextTexts);
    if (dirtyAlpha) setUiAlpha(nextAlpha);
  });

  // ----- render ---------------------------------------------------------------
  return (
    <>
      {Array.from({ length: COUNT }).map((_, i) => (
        <group key={i} ref={groupRefs.current[i]}>
          <HtmlTipCard
            text={uiTexts[i] || ""}
            alpha={0.9} //uiAlpha[i] ?? 1}
            widthPx={100}
            maxLines={3}
            distanceFactor={48}
          />
        </group>
      ))}
    </>
  );
}

// -----------------------------------------------------------------------------
// SHARE CURVE GEOMETRY between Connections and Travellers for perfect sync
// -----------------------------------------------------------------------------
function useCurvesShared(
  anchorRefs: React.RefObject<THREE.Group | null>[],
  colors: string[],
  coreRef: React.RefObject<THREE.Object3D>,
  lift = 2.0,
) {
  const center = useRef(new THREE.Vector3());
  const s = useRef(new THREE.Vector3());
  const dir = useRef(new THREE.Vector3());

  // Precompute deterministic seed vectors per anchor
  const seedVec = (i: number) => {
    let h = 2166136261 ^ i;
    h = Math.imul(h ^ (h >>> 13), 16777619);
    const rx = ((h >>> 0) % 997) / 997;
    h = Math.imul(h ^ (h >>> 13), 16777619);
    const ry = ((h >>> 0) % 991) / 991;
    h = Math.imul(h ^ (h >>> 13), 16777619);
    const rz = ((h >>> 0) % 983) / 983;
    return new THREE.Vector3(rx - 0.5, ry - 0.5, rz - 0.5).normalize();
  };

  // Jitter params per curve (direction wander)
  const jitterSpeed = useMemo(
    () => anchorRefs.map((_, i) => 3.4 + ((i * 97) % 17) * 0.03),
    [anchorRefs],
  );
  const jitterPhase0 = useMemo(
    () => anchorRefs.map((_, i) => ((i * 37) % 29) * 0.21),
    [anchorRefs],
  );

  // Ref that holds the live curve endpoints for all panels
  const curves = useRef<CurveRef[]>(
    anchorRefs.map((ar, i) => ({
      anchorRef: ar,
      color: colors[i] ?? "#E7FF7A",
      control: new THREE.Vector3(),
      end: new THREE.Vector3(),
    })),
  );

  // Recreate array when anchors or colors change (refs are stable)
  useEffect(() => {
    curves.current = anchorRefs.map((ar, i) => ({
      anchorRef: ar,
      color: colors[i] ?? "#E7FF7A",
      control: new THREE.Vector3(),
      end: new THREE.Vector3(),
    }));
  }, [anchorRefs, colors]);

  //   // Update all curve control/endpoints every frame
  //   useFrame((_, dt) => {
  //     coreRef.current?.getWorldPosition(center.current);

  //     for (let i = 0; i < curves.current.length; i++) {
  //       const c = curves.current[i];
  //       c.anchorRef.current?.getWorldPosition(s.current);
  //       dir.current.subVectors(s.current, center.current).normalize();

  //       // Build tangent basis u,v on the sphere
  //       const u = new THREE.Vector3()
  //         .copy(dir.current)
  //         .cross(new THREE.Vector3(0, 1, 0));
  //       if (u.lengthSq() < 1e-6) u.set(1, 0, 0);
  //       u.normalize();
  //       const v = new THREE.Vector3().copy(dir.current).cross(u).normalize();

  //       // Two-frequency angular wander + slow precession
  //       const w1 = 1.1 + jitterSpeed[i] * 0.22;
  //       const w2 = 1.6 + jitterSpeed[i] * 0.15;
  //       const A1 = THREE.MathUtils.degToRad(38);
  //       const A2 = THREE.MathUtils.degToRad(22);
  //       const a =
  //         A1 * Math.sin(performance.now() * 0.001 * w1 + jitterPhase0[i] * 1.7);
  //       const b =
  //         A2 * Math.sin(performance.now() * 0.001 * w2 + jitterPhase0[i] * 0.9);
  //       const precess =
  //         THREE.MathUtils.degToRad(24) *
  //         Math.sin(performance.now() * 0.001 * 0.28 + i * 0.41);

  //       const dirJ = new THREE.Vector3()
  //         .copy(dir.current)
  //         .multiplyScalar(Math.cos(a) * Math.cos(b))
  //         .addScaledVector(u, Math.sin(a))
  //         .addScaledVector(v, Math.sin(b))
  //         .applyAxisAngle(dir.current, precess)
  //         .normalize();

  //       // Small dual-axis orbit to make endpoints "breathe"
  //       const orbitA =
  //         0.035 * Math.sin(performance.now() * 0.001 * (0.7 + 0.13 * i));
  //       const orbitB =
  //         0.026 * Math.cos(performance.now() * 0.001 * (0.9 + 0.17 * i));
  //       const rotAxis = new THREE.Vector3()
  //         .copy(u)
  //         .multiplyScalar(orbitA)
  //         .addScaledVector(v, orbitB)
  //         .normalize();
  //       dirJ.applyAxisAngle(rotAxis, (Math.PI / 2) * 0.06).normalize();

  //       // End point sits on the core surface
  //       c.end.copy(center.current).addScaledVector(dirJ, CORE_RADIUS + 0.001);

  //       // Control point halfway between panel start and end, lifted + swayed
  //       c.control
  //         .copy(s.current)
  //         .lerp(c.end, 0.5)
  //         .add(
  //           new THREE.Vector3(
  //             0,
  //             (CURVE_LIFT || 5.5) +
  //               0.35 *
  //                 Math.sin(
  //                   performance.now() * 0.001 * (w1 * 0.9) + jitterPhase0[i]
  //                 ),
  //             0
  //           )
  //         );

  //       const swayU =
  //         1.3 *
  //         Math.sin(
  //           performance.now() * 0.001 * (0.8 + 0.21 * i) + jitterPhase0[i] * 0.7
  //         );
  //       const swayV =
  //         0.6 *
  //         Math.cos(
  //           performance.now() * 0.001 * (1.1 + 0.17 * i) + jitterPhase0[i] * 1.1
  //         );
  //       c.control.addScaledVector(u, swayU).addScaledVector(v, swayV);
  //     }
  //   });

  useFrame(() => {
    // Get current core center
    coreRef.current?.getWorldPosition(center.current);

    for (let i = 0; i < curves.current.length; i++) {
      const c = curves.current[i];

      // Start at panel anchor
      c.anchorRef.current?.getWorldPosition(s.current);

      // Direction from center toward the panel (ray that hits the ball)
      dir.current.subVectors(s.current, center.current).normalize();

      // Fixed end point on the ball's surface (no motion/jitter)
      c.end
        .copy(center.current)
        .addScaledVector(dir.current, CORE_RADIUS + 0.001);

      // Control point: halfway between start and end, lifted straight up by KNOBS
      c.control
        .copy(s.current)
        .lerp(c.end, 0.5)
        .add(new THREE.Vector3(0, CURVE_LIFT || 0, 0)); // set CURVE_LIFT=0 for perfectly straight
    }
  });

  return curves; // shared ref consumed by Connections + TipTravellers
}

// -----------------------------------------------------------------------------
// OPTIONAL: Minimal gradient line sanity checks (not used in scene)
// -----------------------------------------------------------------------------
function RawGradientLine() {
  /* ...omitted for brevity in annotated build... */ return null;
}
function GradientTest() {
  /* ...omitted for brevity in annotated build... */ return null;
}

// -----------------------------------------------------------------------------
// SCENE COMPOSITION — lights, core, ring of panels, connections, travellers
// -----------------------------------------------------------------------------
function SceneInner({
  onOpenSkill,
  skills,
  autoRotate,
  openCategoryTable,
}: {
  onOpenSkill?: (id: string) => void;
  skills: { id: string; name: string; node?: any }[];
  autoRotate: boolean;
  openCategoryTable: (id: string, title: string) => void;
}) {
  const { camera, size, viewport } = useThree();

  // Treat the provided skills as your top-level categories
  const categories = skills;

  const catSummaries = useMemo(
    () => summarizeCategories(categories),
    [categories],
  );

  // Place category panels evenly around a ring
  const stars = useMemo(() => {
    const N = Math.max(skills.length, 1);
    return skills.map((s, i) => {
      const t = (i * Math.PI * 2) / N;
      return {
        ...s,
        pos: [Math.cos(t) * ORBIT_R, ORBIT_Y, Math.sin(t) * ORBIT_R] as [
          number,
          number,
          number,
        ],
      };
    });
  }, [skills]);

  // Generate stable per-category colors
  const colors = useMemo(() => stars.map((s) => colorFromIdHex(s.id)), [stars]);

  // Core + ring parents
  const coreRef = useRef<THREE.Group>(null!);
  const ringRef = useRef<THREE.Group>(null!);

  // Auto-rotate the ring gently until user interacts
  useFrame((_, dt) => {
    if (ringRef.current && autoRotate) ringRef.current.rotation.y += dt * 0.08;
  });

  // One anchor ref per panel for curve start points
  const anchorRefs = useMemo(
    () => stars.map(() => React.createRef<THREE.Group>()),
    [stars],
  );

  // Share exact curve geometry (control/end points) across ribbons & travellers
  const curves = useCurvesShared(anchorRefs, colors, coreRef, CURVE_LIFT);

  // Build per-category emitters (texts include level + type)
  const emitters = useMemo(
    () =>
      stars.map((s, i) => ({
        color: colors[i],
        texts: stringsForCategoryWithLevelsAndType(s.id, idIndex),
      })),
    [stars, colors],
  );

  return (
    <>
      {/* Scene background + lights */}
      <color attach="background" args={["#0b0f14"]} />
      <ambientLight intensity={0.35} />
      <pointLight position={[0, 0, 0]} intensity={1.6} />

      {/* Core pickleball */}
      <group ref={coreRef} position={[0, CORE_Y, 0]}>
        <RealPickleballCore
          radius={CORE_RADIUS}
          holes={28}
          holeRadius={CORE_RADIUS * 0.085}
          baseHex="#D6E24A"
          emissiveHex="#E8F766"
          emissiveIntensity={0.18}
          backfaceDarken={0.45}
          ambient={0.25}
        />
      </group>

      {/* Title above the core */}
      <Billboard follow lockX lockZ>
        <group renderOrder={9999} position={[0, CORE_Y + CORE_RADIUS + 2.0, 0]}>
          <DreiText
            fontSize={1.8}
            anchorX="center"
            anchorY="bottom"
            color="white"
            material-transparent
            material-depthTest={false}
            material-depthWrite={false}
            material-toneMapped={false}
          >
            Pickleball Skills
          </DreiText>
        </group>
      </Billboard>

      {/* Ring of category panels */}
      <group ref={ringRef}>
        {catSummaries.map((s, i) => (
          <group key={s.id} position={stars[i].pos}>
            {/* Anchor for curve start (slightly above panel center) */}
            <group ref={anchorRefs[i]} position={[0, 2.12, 0]} />

            <PlanetTableOverlay
              id={s.id}
              position={[0, 2.12, 0]}
              node={categories[i]}
              title={s.title}
              distanceFactor={12}
              widthPx={700}
              orientOutwardFrom={[0, 0, 0]}
              onOpenCategoryTable={openCategoryTable}
              accentColor={colors[i]}
            />
          </group>
        ))}
      </group>

      {/* Animated ribbons + moving label cards */}
      <Connections curves={curves} />
      <TipTravellers
        curves={curves}
        speed={KNOBS.travellers.speed}
        emitters={emitters}
        ridersPerEmitter={2}
        camera={camera as THREE.PerspectiveCamera}
        dpr={viewport.dpr}
        viewportHeight={size.height}
      />

      {/* Camera controls */}
      <OrbitControls
        enablePan={false}
        makeDefault
        enableDamping
        dampingFactor={0.08}
      />
    </>
  );
}

// -----------------------------------------------------------------------------
// TOP-LEVEL COMPONENT — wraps Canvas + dialog wiring
// -----------------------------------------------------------------------------
export function GalaxyScene({
  isSmall,
  isLandscape,
  onOpenSkill,
  skills = defaultCategories,
}: {
  isSmall: boolean;
  isLandscape: boolean;
  onOpenSkill?: (id: string) => void;
  skills?: SkillNode[];
}) {
  // Auto-rotate until user touches/wheels/keys once
  const [autoRotate, setAutoRotate] = useState(true);

  // Category table dialog state
  const [tableOpen, setTableOpen] = useState(false);
  const [tableCfg, setTableCfg] = useState<{
    mode: "level" | "category";
    level: SkillLevel | null;
    categoryId: string | null;
    titleOverride?: string;
  }>({ mode: "level", level: null, categoryId: null });

  const openCategoryTable = (id: string, title: string) => {
    setTableCfg({
      mode: "category",
      level: null,
      categoryId: id,
      titleOverride: `Skills for ${title}`,
    });
    setTableOpen(true);
  };

  // One-off listener to disable auto-rotate on first interaction
  useEffect(() => {
    if (!autoRotate) return;
    const stop = () => setAutoRotate(false);
    window.addEventListener("pointerdown", stop, { once: true });
    window.addEventListener("wheel", stop, { once: true, passive: true });
    window.addEventListener("keydown", stop, { once: true });
    return () => {
      window.removeEventListener("pointerdown", stop);
      window.removeEventListener("wheel", stop);
      window.removeEventListener("keydown", stop);
    };
  }, [autoRotate]);

  return (
    <>
      <Canvas
        style={{ width: "100%", height: "100%" }}
        dpr={[1, 1.75]}
        gl={{ antialias: true, powerPreference: "high-performance" }}
        onCreated={(state:any) => {
          state.gl.toneMapping = THREE.NoToneMapping;
          // Keep colors consistent & bright
          // ts-expect-error three typings vary by version
          state.gl.outputColorSpace = THREE.SRGBColorSpace;
        }}
        camera={{ position: [0, 18, 42], fov: 50, near: 0.1, far: 2000 }}
      >
        <ZProvider>
          <SceneInner
            onOpenSkill={onOpenSkill}
            openCategoryTable={openCategoryTable}
            skills={skills}
            autoRotate={autoRotate}
          />
        </ZProvider>
      </Canvas>

      {/* Category-level detail dialog */}
      <LevelTableDialog
        isSmall={isSmall}
        isLandscape={isLandscape}
        skillsRoot={skillsRoot}
        open={tableOpen}
        onClose={() => setTableOpen(false)}
        mode={tableCfg.mode}
        level={tableCfg.level}
        categoryId={tableCfg.categoryId}
        titleOverride={tableCfg.titleOverride}
        idIndex={idIndex}
      />
    </>
  );
}
